<template>
  <!-- Sidebar derecho flotante REUTILIZABLE (chrome compartido estilo Excalidraw).
       Generalizado desde el ViewerSidebarDesign de solar-park: cabecera con
       pestañas (botones de icono) + fijar (pin, opcional) + cerrar, y un cuerpo
       con un SLOT por pestaña (el host rellena cada una). El estado de pestaña
       activa es interno pero sincronizable con el padre vía :active-tab.sync. -->
  <div class="epc-sidebar" :class="{ 'is-docked': docked }">
    <div class="epc-sidebar-header">
      <!-- Pestañas: cada botón es una entrada de `tabs` -->
      <div class="epc-sidebar-tabs">
        <button v-for="tab in tabs" :key="tab.key"
          class="epc-sidebar-tab"
          :class="{ 'is-active': tab.key === currentTab }"
          :aria-label="tab.label || tab.key"
          :title="tab.label || tab.key"
          @click="_SelectTab(tab)">
          <b-icon pack="fas" :icon="tab.icon" size="is-small"></b-icon>
        </button>
      </div>

      <div class="epc-sidebar-header-actions">
        <!-- Acciones genéricas de cabecera (icono + tooltip), ANTES del pin. El
             host las define por `header-actions` y maneja `epc-action(key)`. El
             icono/estado activo los controla el host (p. ej. eye / eye-slash). -->
        <template v-for="a in headerActions">
          <b-tooltip v-if="a.tooltip" :key="a.key" :label="a.tooltip" position="is-bottom">
            <button class="epc-sidebar-icon-btn" :class="{ 'is-active': a.active }"
              :aria-label="a.tooltip" @click="$emit('epc-action', a.key)">
              <b-icon :pack="a.pack || 'fas'" :icon="a.icon" size="is-small"></b-icon>
            </button>
          </b-tooltip>
          <button v-else :key="a.key + '-nt'" class="epc-sidebar-icon-btn"
            :class="{ 'is-active': a.active }" :aria-label="a.key"
            @click="$emit('epc-action', a.key)">
            <b-icon :pack="a.pack || 'fas'" :icon="a.icon" size="is-small"></b-icon>
          </button>
        </template>

        <!-- Fijar (pin): acopla el panel y reduce el lienzo. Opcional. -->
        <button v-if="pinnable"
          class="epc-sidebar-icon-btn"
          :class="{ 'is-active': docked }"
          :aria-label="$t('pin')"
          @click="_TogglePin">
          <b-icon pack="fas" icon="thumbtack" size="is-small"></b-icon>
        </button>

        <!-- Cerrar -->
        <button class="epc-sidebar-icon-btn"
          :aria-label="$t('close')"
          @click="$emit('epc-close', true)">
          <b-icon pack="fas" icon="xmark" size="is-small"></b-icon>
        </button>
      </div>
    </div>

    <div class="epc-sidebar-divider"></div>

    <!-- Cuerpo: un slot con nombre por pestaña. Se montan todas y solo se
         muestra la activa (v-show) para preservar scroll/estado al cambiar. -->
    <div class="epc-sidebar-body">
      <div v-for="tab in tabs" :key="tab.key"
        v-show="tab.key === currentTab" class="epc-sidebar-tabpane">
        <slot :name="tab.key"></slot>
      </div>

      <!-- Placeholder si no hay pestañas (o ninguna con contenido). -->
      <div v-if="!tabs || !tabs.length" class="epc-sidebar-empty">
        <b-icon pack="fas" icon="layer-group" custom-size="fa-2x" class="epc-sidebar-empty-icon"></b-icon>
        <p class="epc-sidebar-empty-text">{{ $t('empty_panel') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WidgetSidebar",

  props: {
    // [{ key, icon, label? }] — cada entrada = un botón de pestaña + su slot.
    tabs: { type: Array, default: () => [] },
    // Pestaña activa (sincronizable: :active-tab.sync). Si no se pasa, arranca
    // en la primera pestaña.
    activeTab: { type: String, default: null },
    // Acciones genéricas de cabecera (antes del pin): [{ key, icon, tooltip?, pack?, active? }].
    // Al pulsar emite `epc-action(key)`; el host controla icono/estado (ver layers eye).
    headerActions: { type: Array, default: () => [] },
    // Si true muestra el botón de fijar (pin) → el host acopla/encoge el lienzo.
    pinnable: { type: Boolean, default: false },
    // Fijado (acoplado) — lo controla el host.
    docked: { type: Boolean, default: false },
  },

  data() {
    return {
      // Estado interno; se mantiene en sync con la prop `activeTab` cuando el
      // host la usa, y cae a la primera pestaña por defecto.
      internalTab: this.activeTab || (this.tabs[0] && this.tabs[0].key) || null,
    }
  },

  computed: {
    currentTab() {
      return this.activeTab || this.internalTab
    },
  },

  watch: {
    activeTab(val) {
      if (val) this.internalTab = val
    },
  },

  methods: {
    _SelectTab(tab) {
      this.internalTab = tab.key
      this.$emit('update:active-tab', tab.key)
      this.$emit('epc-tab', tab.key)
    },
    _TogglePin() {
      this.$emit('epc-pin', !this.docked)
    },
  },
}
</script>

<style scoped>
.epc-sidebar {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 340px;
  max-width: 85vw;
  background-color: #ffffff;
  border-left: 1px solid #e3e3e8;
  box-shadow: -2px 0 8px 0 rgba(0, 0, 0, 0.08);
  z-index: 7;
  display: flex;
  flex-direction: column;
}

/* Fijado (pin): sale del overlay y se acopla al flujo (hermano flex del área del visor),
   así el lienzo se encoge en vez de quedar tapado. */
.epc-sidebar.is-docked {
  position: relative;
  top: auto;
  right: auto;
  bottom: auto;
  height: 100%;
  flex: 0 0 340px;
  z-index: auto;
}

.epc-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
}

/* Control segmentado: track gris + pestaña activa = pastilla blanca elevada
   (estilo iOS/Figma), icono activo en el azul de marca. */
.epc-sidebar-tabs {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 10px;
  background-color: #f2f2f7;
}

.epc-sidebar-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 30px;
  border: none;
  border-radius: 7px;
  background-color: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.epc-sidebar-tab:hover:not(.is-active) {
  background-color: #e9e9ef;
  color: #495057;
}

.epc-sidebar-tab.is-active {
  background-color: #ffffff;
  color: #3a6bc8;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.10);
}

.epc-sidebar-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.epc-sidebar-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.epc-sidebar-icon-btn:hover {
  background-color: #f2f2f7;
  color: #3a6bc8;
}

.epc-sidebar-icon-btn.is-active {
  color: #3a6bc8;
}

.epc-sidebar-divider {
  height: 1px;
  margin: 0 12px;
  background-color: #ececf1;
}

.epc-sidebar-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  /* Scrollbar fina y sutil (inset con borde transparente + background-clip). */
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: #d4d4dd transparent;
}

.epc-sidebar-body::-webkit-scrollbar {
  width: 10px;
}

.epc-sidebar-body::-webkit-scrollbar-track {
  background: transparent;
}

.epc-sidebar-body::-webkit-scrollbar-thumb {
  background-color: #d4d4dd;
  border-radius: 999px;
  border: 3px solid transparent;         /* deja ver ~4px de pulgar */
  background-clip: padding-box;
}

.epc-sidebar-body::-webkit-scrollbar-thumb:hover {
  background-color: #b9b9c6;
}

.epc-sidebar-tabpane {
  height: 100%;
}

.epc-sidebar-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
  color: #9a9aa2;
}

.epc-sidebar-empty-icon {
  color: #c7c7d1;
  margin-bottom: 8px;
}

.epc-sidebar-empty-text {
  font-size: 13px;
}
</style>
